/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useEffect, useRef } from 'react';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import { Layout, TextLink } from '@oneaudi/unified-web-components';
import { FootnoteContextProvider, useInViewEffect } from '@oneaudi/feature-app-utils';
import type { UnifiedThemeType } from '@oneaudi/unified-web-common';
import type { VueFormatterFeatureServiceInterfaceV1 } from '@oneaudi/vue-formatter-service/lib/cjs/v1/vue-formatter-feature-service';
import { UeElement } from '@oneaudi/falcon-tools';
import type { EditorialTextProps } from '../../@types/types';
import { processBodyText } from './processBodyText';
import { getValidCtaAriaLabel } from '../../utils/ariaLabelHelper';
import { getThemeName } from '../../utils/alphaHelper';
import type { ThemeName } from '@oneaudi/unified-web-alpha-common/dist/cjs/components/theme-provider/theme';
import {
  ContainerDiv,
  ButtonContainerDiv,
  TextWrapperDiv,
  TextLayout,
  StyledTextLayoutItem,
} from './styledComponents';
import { ConsumptionAndEmission } from './ConsumptionAndEmission';

export const EditorialText = ({
  content,
  footnoteReferenceService,
  vueFormatterService,
  localeService,
  trackingService,
}: EditorialTextProps) => {
  const eTextRef = useRef<HTMLDivElement | null>(null);
  const eTextContent = useRef<HTMLDivElement | null>(null);
  const {
    bodyText,
    theme,
    justification,
    ctaButtonLabel,
    ctaButtonUrl,
    ctaButtonOpenInNewTab,
    ariaLabel,
    consuptionAndEmission,
    disclaimer,
    bottomMargin,
    reduceTextWidth,
  } = content;

  const themeActive = getThemeName((theme || 'light') as ThemeName);

  const processedCopy = processBodyText(bodyText);

  const getFirstLineFromBodyText = (text: string): string => {
    try {
      // Create a temporary DOM element to safely extract text content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = text;
      const cleanText = tempDiv.textContent || tempDiv.innerText || '';

      // Split by line breaks and take the first non-empty line
      const lines = cleanText.split(/\r?\n|\r/);
      const firstLine = lines.find((line) => line.trim().length > 0);

      return firstLine ? firstLine.trim() : '';
    } catch {
      return '';
    }
  };

  const getFirstElementInnerText = (): string => {
    try {
      let firstText = '';

      // Check if eTextContent exists and has current ref
      if (!eTextContent || !eTextContent.current) {
        // Fallback: use first line of bodyText if no DOM content
        return getFirstLineFromBodyText(bodyText || '');
      }

      // Check if there's any content at all
      if (!eTextContent.current.firstChild) {
        // Fallback: use first line of bodyText if no DOM content
        return getFirstLineFromBodyText(bodyText || '');
      }

      // Look for title elements (h1, h2, h3, h4, h5, h6) first
      const titleElements = eTextContent.current.querySelectorAll('h1, h2, h3, h4, h5, h6');
      if (titleElements.length > 0) {
        const firstTitle = titleElements[0] as HTMLElement;
        firstText = firstTitle.innerText || firstTitle.textContent || '';
        if (firstText.trim()) {
          return firstText.trim();
        }
      }

      // Case 1: Direct element (non-DIV)
      const firstChild = eTextContent.current.firstChild as HTMLElement;
      if (firstChild && firstChild.tagName && firstChild.tagName !== 'DIV') {
        firstText = firstChild.innerText || '';
        if (firstText.trim()) {
          return firstText.trim();
        }
      }

      // Case 2: Nested structure - check each level safely
      const level1 = eTextContent.current.firstChild;
      if (!level1) {
        return getFirstLineFromBodyText(bodyText || '');
      }

      const level2 = level1.firstChild;
      if (!level2) {
        return getFirstLineFromBodyText(bodyText || '');
      }

      const level3 = level2.firstChild;
      if (!level3) {
        return getFirstLineFromBodyText(bodyText || '');
      }

      // Safely get innerHTML from the nested element
      const nestedElement = level3 as HTMLElement;
      if (nestedElement && nestedElement.innerHTML !== undefined) {
        firstText = nestedElement.innerHTML;
        // Clean HTML from the result using safe DOM parsing
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = firstText;
        const cleanText = tempDiv.textContent || tempDiv.innerText || '';
        if (cleanText.trim()) {
          return cleanText.trim();
        }
      }

      // Final fallback: use first line of bodyText
      return getFirstLineFromBodyText(bodyText || '');
    } catch {
      // Last resort fallback
      return getFirstLineFromBodyText(bodyText || '');
    }
  };

  const getUrlExtension = (url: string) => {
    return url?.split(/[#?]/)[0].split('.')?.pop()?.trim();
  };

  const setEventAction = (ctaUrl: string) => {
    const isExternalURL = (url: string) => new URL(url).origin !== window.location.origin;
    if (getUrlExtension(ctaUrl) === 'pdf' || getUrlExtension(ctaUrl) === 'xlsx') return 'download';

    if (ctaUrl === '') return 'content';

    if (isExternalURL(ctaUrl)) return 'exit_link';

    return 'internal_link';
  };

  const isFullUrl = (url: string) => {
    const expression = /(www|http:|https:)+[^\s]+[\w]/gi;
    const regex = new RegExp(expression);

    if (url.match(regex)) {
      return url;
    }

    const newUrl = new URL(url, window.location.href);
    return newUrl.href;
  };

  const addTrackingClickEvent = (cta: HTMLAnchorElement) => {
    const url = cta.href;

    const dataLayerEvent: any = {
      eventInfo: {
        eventAction: setEventAction(url),
        eventName: `editorial text - click ${setEventAction(url) === 'download' ? 'to download' : 'on text link'}`,
      },
      attributes: {
        componentName: 'editorial-text',
        label: cta.innerText,
        targetURL: isFullUrl(url),
        clickID: '',
        elementName: 'text link',
        value: cta.parentElement?.previousElementSibling?.innerHTML
          ? cta.parentElement?.previousElementSibling?.innerHTML
          : getFirstElementInnerText(),
        pos: '',
        relatedElements:
          getUrlExtension(url) === 'pdf' || getUrlExtension(url) === 'xlsx'
            ? {
                elementName: `${getUrlExtension(url)} file`,
                elementValue: url
                  .split('/')
                  .pop()
                  ?.replace(/.pdf|.xlsx/gi, ''),
              }
            : [cta.innerHTML],
      },
    };

    cta.onclick = () => {
      trackingService?.track({
        event: dataLayerEvent,
      });
    };
  };

  useInViewEffect(() => {
    const readyEvent = {
      event: {
        attributes: {
          componentName: 'editorial-text',
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: getFirstElementInnerText(),
        },
        eventInfo: {
          eventAction: 'impression',
          eventName: `editorial text - impression`,
        },
      },
    };
    trackingService?.track(readyEvent);
  });

  const addTrackingClickEventCallback = React.useCallback(
    (cta: HTMLAnchorElement) => {
      addTrackingClickEvent(cta);
    },
    [addTrackingClickEvent],
  );

  useEffect(() => {
    const ctaList = eTextRef?.current?.querySelectorAll('a');
    if (ctaList) {
      ctaList.forEach((cta: HTMLAnchorElement) => {
        addTrackingClickEventCallback(cta);
      });
    }

    // Fix empty aria-label attributes in rendered footnotes using MutationObserver
    if (eTextRef?.current) {
      const fixFootnoteAriaLabels = () => {
        const footnoteLinks = eTextRef.current?.querySelectorAll(
          'a[aria-label=""], a:not([aria-label])',
        );
        if (footnoteLinks) {
          footnoteLinks.forEach((element: Element) => {
            const link = element as HTMLAnchorElement;
            // Check if this looks like a footnote link
            const isFootnoteLink =
              link.className.includes('footnote') ||
              link.href.includes('#') ||
              link.closest('[class*="footnote"]');

            if (isFootnoteLink) {
              const linkText = link.textContent?.trim() || '';
              let newAriaLabel = 'Footnote reference';

              if (linkText) {
                newAriaLabel = `Footnote: ${linkText}`;
              } else if (link.href.includes('#')) {
                const footnoteId = link.href.split('#')[1];
                if (footnoteId) {
                  newAriaLabel = `Footnote reference ${footnoteId}`;
                }
              }

              link.setAttribute('aria-label', newAriaLabel);
            }
          });
        }
      };

      // Fix immediately
      fixFootnoteAriaLabels();

      // Set up MutationObserver to watch for footnote additions
      const observer = new MutationObserver(() => {
        fixFootnoteAriaLabels();
      });

      observer.observe(eTextRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-label'],
      });

      // Cleanup
      return () => {
        observer.disconnect();
      };
    }

    // Return undefined cleanup function when no observer is created
    return undefined;
  }, [addTrackingClickEventCallback]);

  return (
    <div data-theme-name={themeActive}>
      <FootnoteContextProvider footnoteReferenceService={footnoteReferenceService}>
        <ThemeProvider theme={{ name: themeActive } as UnifiedThemeType}>
          <ContainerDiv
            theme={themeActive}
            justification={justification}
            bottomMargin={bottomMargin}
            reduceTextWidth={reduceTextWidth}
            ref={eTextRef}
          >
            <TextLayout reduceTextWidth={reduceTextWidth} theme={themeActive}>
              <Layout data-test-id="title" direction="column">
                <StyledTextLayoutItem basis="100%">
                  <div>
                    <TextWrapperDiv
                      data-testid="textWrapper"
                      align={justification}
                      theme={themeActive}
                      ref={eTextContent}
                    >
                      {bodyText ? processedCopy : null}
                    </TextWrapperDiv>
                  </div>
                </StyledTextLayoutItem>

                {ctaButtonUrl?.length > 0 && ctaButtonLabel?.length > 0 && (
                  <StyledTextLayoutItem basis="auto">
                    <ButtonContainerDiv align={justification}>
                      <TextLink
                        data-testid={ariaLabel || ctaButtonLabel}
                        variant="standalone"
                        size="md"
                        target={ctaButtonOpenInNewTab === 'newTab' ? '_blank' : '_self'}
                        href={ctaButtonUrl}
                        onClick={() => {}}
                        icon="forward"
                        iconPosition="trailing"
                        aria-label={getValidCtaAriaLabel(ariaLabel, ctaButtonLabel)}
                      >
                        <UeElement type="text" property="ctaButtonLabel" label="CTA Label">
                          {ctaButtonLabel}
                        </UeElement>
                      </TextLink>
                    </ButtonContainerDiv>
                  </StyledTextLayoutItem>
                )}
                {consuptionAndEmission?.length > 0 || disclaimer?.length > 0 ? (
                  <ConsumptionAndEmission
                    consuptionAndEmission={consuptionAndEmission}
                    disclaimer={disclaimer}
                    theme={themeActive}
                    vueFormatterService={
                      vueFormatterService as VueFormatterFeatureServiceInterfaceV1
                    }
                    align={justification}
                    localeService={localeService!}
                  />
                ) : null}
              </Layout>
            </TextLayout>
          </ContainerDiv>
        </ThemeProvider>
      </FootnoteContextProvider>
    </div>
  );
};
